import { Table, type TableProps } from 'antd';
import { type SizeType } from 'antd/es/config-provider/SizeContext';
import { type ColumnGroupType, type ColumnType } from 'antd/es/table';
import {
  type FilterValue,
  type SorterResult,
  type TableCurrentDataSource,
  type TableRowSelection,
} from 'antd/es/table/interface';
import React, { type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Loading-state convention across the app:
 *  - <Skeleton active>         — page-level initial load (renders nothing until data arrives)
 *  - <Spin spinning={bool}>    — inline async overlay after first render
 *  - <TableComponent isLoading> — table-level loading indicator (this component)
 */

const DEFAULT_PAGE_SIZE = 10;

function TableComponent({
  columns,
  dataSource,
  isLoading,
  summary,
  rowSelection,
  emptyText,
  size,
  scroll,
  onChange,
  pagination,
  rowKey,
  expandable,
  onRow,
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: {
  columns: (ColumnGroupType<object> | ColumnType<object>)[];
  dataSource: readonly object[];
  isLoading: boolean;
  summary?: (pageData: readonly object[]) => ReactNode;
  rowSelection?: TableRowSelection<object>;
  emptyText?: ReactNode;
  size?: SizeType;
  scroll?: { x?: number | string | true; y?: number | string };
  onChange?: (
    _pagination: object,
    _filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<object> | SorterResult<object>[],
    extra: TableCurrentDataSource<object>,
  ) => void;
  pagination?:
    | false
    | {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, pageSize: number) => void;
      };
  rowKey?: TableProps<object>['rowKey'];
  expandable?: TableProps<object>['expandable'];
  onRow?: TableProps<object>['onRow'];
  defaultPageSize?: number;
}): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();

  let tablePagination: TableProps<object>['pagination'] = false;

  if (pagination) {
    tablePagination = {
      placement: ['topCenter', 'bottomCenter'],
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
      showSizeChanger: true,
      onChange: pagination.onChange,
    };
  } else if (pagination === undefined) {
    tablePagination = {
      placement: ['topCenter', 'bottomCenter'],
      defaultCurrent: Number(searchParams.get('page')) || 1,
      defaultPageSize: Number(searchParams.get('pageSize')) || defaultPageSize,
      onChange: (page: number, pageSize: number): void => {
        searchParams.set('page', page.toString());
        searchParams.set('pageSize', pageSize.toString());

        setSearchParams(searchParams);
      },
    };
  }

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      summary={summary}
      rowSelection={rowSelection}
      locale={emptyText !== undefined ? { emptyText } : undefined}
      rowKey={rowKey}
      expandable={expandable}
      pagination={tablePagination}
      loading={isLoading}
      size={size}
      scroll={scroll}
      onChange={onChange}
      onRow={onRow}
    />
  );
}

export default TableComponent;
